import React, { useRef, useEffect, useCallback } from "react";
import { Share2, Download, Link as LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");

// Controlled brag card for a freshly-unlocked co-op achievement. Parent owns `open`.
export default function AchievementCard({ achievement, user, open, onOpenChange }) {
  const canvasRef = useRef(null);
  const shareUrl = `${window.location.origin}/app/multiplayer`;
  const shortUrl = shareUrl.replace(/^https?:\/\//, "");
  const a = achievement || {};

  const draw = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const W = 1200, H = 630;
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#0b1026"); grad.addColorStop(0.55, "#123047"); grad.addColorStop(1, "#0e7a4f");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    const rad = ctx.createRadialGradient(300, 330, 30, 300, 330, 520);
    rad.addColorStop(0, "rgba(52,211,153,0.30)"); rad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = rad; ctx.fillRect(0, 0, W, H);
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(124,247,255,0.92)"; ctx.font = "bold 32px Inter, system-ui, sans-serif";
    ctx.fillText("🛡 GAMEVIEW STUDIO · CO-OP", 60, 52);
    ctx.fillStyle = "#34d399"; ctx.font = "900 46px Inter, system-ui, sans-serif";
    ctx.fillText("ACHIEVEMENT UNLOCKED", 60, 108);
    // big badge glyph
    ctx.textBaseline = "middle"; ctx.font = "220px serif";
    ctx.fillText(a.icon || "🏅", 90, 360);
    // name
    ctx.textBaseline = "alphabetic"; ctx.fillStyle = "#fff"; ctx.font = "bold 76px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(a.name || "Champion", 18), 380, 310);
    ctx.fillStyle = "rgba(255,255,255,0.82)"; ctx.font = "36px Inter, system-ui, sans-serif";
    ctx.fillText(truncate((user?.name || "A hero") + " earned this", 30), 380, 375);
    ctx.fillStyle = "#ffd166"; ctx.font = "bold 30px Inter, system-ui, sans-serif";
    ctx.fillText("Revive · Slay · Fell bosses together", 380, 430);
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.textBaseline = "top"; ctx.font = "28px Inter, system-ui, sans-serif";
    ctx.fillText(`▶ Squad up — ${truncate(shortUrl, 46)}`, 60, 580);
  }, [a.icon, a.name, user?.name, shortUrl]);

  useEffect(() => { if (open) { const t = setTimeout(draw, 60); return () => clearTimeout(t); } }, [open, draw]);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
  const download = async () => {
    const blob = await toBlob(); if (!blob) return;
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url; el.download = `coop-${(a.name || "achievement").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
    el.click(); URL.revokeObjectURL(url);
    toast.success("Achievement card downloaded!");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — brag anywhere!"); }
    catch { toast.error("Couldn't copy the link."); }
  };
  const nativeShare = async () => {
    const text = `I just unlocked "${a.name}" in GameView Studio co-op! 🛡`;
    try {
      const blob = await toBlob();
      const file = blob ? new File([blob], "coop-achievement.png", { type: "image/png" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: a.name, text, url: shareUrl });
      } else {
        await navigator.share({ title: a.name, text, url: shareUrl });
      }
    } catch { /* cancelled / unsupported */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="achievement-card-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-emerald-400" /> Share your achievement</DialogTitle>
          <DialogDescription>Download or share a brag card celebrating your unlocked co-op badge.</DialogDescription>
        </DialogHeader>
        <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="achievement-card-canvas" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={download} className="rounded-full gap-1.5 flex-1 bg-gradient-to-r from-emerald-500 to-sky-500" data-testid="achievement-card-download"><Download className="w-4 h-4" /> Download image</Button>
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="achievement-card-copy"><LinkIcon className="w-4 h-4" /> Copy link</Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="achievement-card-native"><Share2 className="w-4 h-4" /> Share…</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
